/**
 * Malfreund theme core. Loaded with `defer`, no dependencies, only global: window.mfTheme
 * (routes, strings, moneyFormat … are set inline in layout/theme.liquid).
 *
 * mfTheme.on(name, fn) → off()   mfTheme.emit(name, detail)   – CustomEvents on `document`
 * mfTheme.formatMoney(cents[, format])   mfTheme.lockScroll() / unlockScroll()
 * mfTheme.reveal(root)   mfTheme.Drawer (base class of <mf-drawer>)
 *
 * Events: mf:cart:open {opener?} · mf:cart:updated {cart} · mf:variant:change {productId, variant}
 *         mf:drawer:open / mf:drawer:close {drawer} (bubble from the drawer element)
 */
(() => {
  'use strict';

  const mf = (window.mfTheme = window.mfTheme || {});
  const doc = document;
  const root = doc.documentElement;

  /* Events ----------------------------------------------------------------- */
  mf.on = (name, fn) => {
    doc.addEventListener(name, fn);
    return () => doc.removeEventListener(name, fn);
  };
  mf.emit = (name, detail = {}) => doc.dispatchEvent(new CustomEvent(name, { detail }));

  /* Money: cents + Shopify money format → string (may contain HTML) --------- */
  mf.formatMoney = (cents, format = mf.moneyFormat || '{{amount}}') => {
    const num = (decimals, thousands, decimal) => {
      const [int, frac] = (Number(cents) / 100).toFixed(decimals).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    const styles = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_no_decimals_with_space_separator: [0, ' ', ','],
      amount_with_space_separator: [2, ' ', ','],
      amount_with_period_and_space_separator: [2, ' ', '.'],
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => (styles[key] ? num(...styles[key]) : match));
  };

  /* Scroll lock (reference-counted, compensates the scrollbar width) -------- */
  let locks = 0;
  mf.lockScroll = () => {
    if (locks++) return;
    root.style.setProperty('--mf-scrollbar-w', `${window.innerWidth - root.clientWidth}px`);
    root.classList.add('mf-scroll-lock');
  };
  mf.unlockScroll = () => {
    if (!locks || --locks) return;
    root.classList.remove('mf-scroll-lock');
    root.style.removeProperty('--mf-scrollbar-w');
  };

  /* Inert everything outside `el` (siblings of el and of its ancestors) ----- */
  const setOutsideInert = (el, on) => {
    if (!on) {
      doc.querySelectorAll('[data-mf-inert]').forEach((node) => {
        node.inert = false;
        node.removeAttribute('data-mf-inert');
      });
      return;
    }
    for (let node = el; node && node !== doc.body; node = node.parentElement) {
      for (const sibling of node.parentElement.children) {
        if (sibling === node || sibling.inert || /^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(sibling.tagName)) continue;
        sibling.inert = true;
        sibling.setAttribute('data-mf-inert', '');
      }
    }
  };

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  /* Drawer / dialog -----------------------------------------------------------
   * <mf-drawer id="X" class="mf-drawer [mf-drawer--left | mf-drawer--dialog]">
   *   <div class="mf-drawer__overlay" data-mf-drawer-close></div>
   *   <div class="mf-drawer__panel" role="dialog" aria-modal="true" aria-labelledby="…" tabindex="-1">…</div>
   * </mf-drawer>
   * Open:  click on [data-mf-drawer-open="X"] (add aria-controls="X" → aria-expanded is kept in sync)
   * Close: click on [data-mf-drawer-close] inside, the overlay, or ESC. Focus returns to the opener.
   * Initial focus: [data-mf-autofocus] inside, otherwise the panel.
   */
  class Drawer extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-mf-drawer-close]')) this.close();
      });
      this.addEventListener('keydown', (event) => this.onKeydown(event));
    }

    get panel() {
      return this.querySelector('.mf-drawer__panel') || this;
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    open(opener) {
      if (this.isOpen) return;
      const other = Drawer.current;
      this.opener = opener || doc.activeElement;
      if (other && other !== this) {
        // Only one drawer at a time; inherit the opener when ours sits inside the one being closed
        if (other.contains(this.opener)) this.opener = other.opener;
        other.close(false);
      }
      Drawer.current = this;
      this.setAttribute('open', '');
      mf.lockScroll();
      setOutsideInert(this, true);
      this.syncTriggers(true);
      const target = this.querySelector('[data-mf-autofocus]') || this.panel;
      requestAnimationFrame(() => target.focus({ preventScroll: true }));
      this.dispatchEvent(new CustomEvent('mf:drawer:open', { bubbles: true, detail: { drawer: this } }));
    }

    close(restoreFocus = true) {
      if (!this.isOpen) return;
      const opener = this.opener;
      this.teardown();
      this.syncTriggers(false);
      if (restoreFocus && opener && opener.isConnected) opener.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('mf:drawer:close', { bubbles: true, detail: { drawer: this } }));
    }

    toggle(opener) {
      if (this.isOpen) this.close();
      else this.open(opener);
    }

    teardown() {
      this.removeAttribute('open');
      this.opener = null;
      if (Drawer.current === this) Drawer.current = null;
      mf.unlockScroll();
      setOutsideInert(this, false);
    }

    syncTriggers(expanded) {
      if (!this.id) return;
      doc.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((el) => el.setAttribute('aria-expanded', String(expanded)));
    }

    onKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      // Focus trap
      const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length);
      const first = items[0];
      const last = items[items.length - 1];
      const active = doc.activeElement;
      if (!first) {
        event.preventDefault();
      } else if (event.shiftKey && (active === first || active === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    disconnectedCallback() {
      // e.g. the theme editor re-renders the section while the drawer is open
      if (this.isOpen) this.teardown();
    }
  }
  Drawer.current = null;
  mf.Drawer = Drawer;
  customElements.define('mf-drawer', Drawer);

  /* Header: .is-scrolled once the page has moved ----------------------------- */
  customElements.define(
    'mf-header',
    class extends HTMLElement {
      connectedCallback() {
        this.onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 4);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
      }
    }
  );

  /* Delegated clicks: drawer openers, cart toggle, cross-page anchors -------- */
  const cartDrawer = () => {
    const el = doc.getElementById('MfCartDrawer');
    return el && typeof el.open === 'function' ? el : null;
  };

  doc.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const opener = target.closest('[data-mf-drawer-open]');
    if (opener) {
      const drawer = doc.getElementById(opener.getAttribute('data-mf-drawer-open'));
      if (drawer && typeof drawer.open === 'function') {
        event.preventDefault();
        drawer.open(opener);
      }
      return;
    }

    // The cart button stays a plain link unless a cart drawer exists
    const cartToggle = target.closest('[data-mf-cart-toggle]');
    if (cartToggle) {
      if (cartDrawer()) {
        event.preventDefault();
        mf.emit('mf:cart:open', { opener: cartToggle });
      }
      return;
    }

    // "/#faq" links: stay on this page when the target section exists here
    const anchor = target.closest('a[data-mf-anchor]');
    if (anchor && anchor.hash.length > 1 && anchor.pathname !== window.location.pathname) {
      let id = anchor.hash.slice(1);
      try {
        id = decodeURIComponent(id);
      } catch (error) {
        /* keep the raw id */
      }
      if (doc.getElementById(id)) {
        event.preventDefault();
        window.location.hash = anchor.hash;
      }
    }
  });

  /* Cart events --------------------------------------------------------------- */
  mf.on('mf:cart:open', (event) => {
    const drawer = cartDrawer();
    if (drawer) drawer.open(event.detail && event.detail.opener);
  });

  mf.on('mf:cart:updated', (event) => {
    const cart = event.detail && event.detail.cart;
    if (!cart || typeof cart.item_count !== 'number') return;
    const count = cart.item_count;
    const label = mf.strings && mf.strings.cartWithCount;
    doc.querySelectorAll('[data-mf-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
    if (label) {
      doc.querySelectorAll('[data-mf-cart-toggle]').forEach((el) => el.setAttribute('aria-label', label.replace('[count]', count)));
    }
  });

  /* Reveal on scroll: adds .is-visible once (doodle line-draw, .mf-reveal) ---- */
  const observer =
    'IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px' }
        )
      : null;

  mf.reveal = (scope = doc) => {
    scope.querySelectorAll('[data-mf-reveal]:not(.is-visible), .mf-reveal:not(.is-visible)').forEach((el) => {
      if (observer) observer.observe(el);
      else el.classList.add('is-visible');
    });
  };
  mf.reveal();

  /* Videos that play only while visible (video.mf-inview-video: muted loop) ---- */
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const videoObserver =
    'IntersectionObserver' in window && !reduceMotion
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              if (!entry.isIntersecting) {
                video.pause();
                return;
              }
              const playing = video.play();
              // Autoplay refused (e.g. power saving): offer the controls instead
              if (playing && playing.catch) playing.catch(() => (video.controls = true));
            });
          },
          { threshold: 0.35 }
        )
      : null;

  // A video may bring a landscape cut (data-wide-src / data-wide-poster) for a wide frame
  const fitVideoToFrame = (video) => {
    const wideSrc = video.dataset.wideSrc;
    if (!wideSrc) return;
    const box = video.getBoundingClientRect();
    if (!box.height) return;
    const shape = box.width > box.height * 1.2 ? 'wide' : 'tall';
    if ((video.dataset.mfShape || 'tall') === shape) return;
    if (!video.dataset.tallSrc) {
      const source = video.querySelector('source');
      video.dataset.tallSrc = source ? source.src : video.currentSrc || video.src;
      video.dataset.tallPoster = video.poster;
    }
    video.dataset.mfShape = shape;
    const wasPlaying = !video.paused;
    video.querySelectorAll('source').forEach((source) => source.remove());
    video.poster = shape === 'wide' ? video.dataset.widePoster : video.dataset.tallPoster;
    video.src = shape === 'wide' ? wideSrc : video.dataset.tallSrc;
    video.load();
    if (wasPlaying) {
      const playing = video.play();
      if (playing && playing.catch) playing.catch(() => {});
    }
  };

  mf.inviewVideos = (scope = doc) => {
    scope.querySelectorAll('video.mf-inview-video').forEach((video) => {
      video.muted = true;
      fitVideoToFrame(video);
      if (videoObserver) videoObserver.observe(video);
      else video.controls = true; // reduced motion: nothing moves until the visitor presses play
    });
  };
  mf.inviewVideos();

  let videoResizeTimer = 0;
  window.addEventListener('resize', () => {
    clearTimeout(videoResizeTimer);
    videoResizeTimer = setTimeout(() => doc.querySelectorAll('video.mf-inview-video').forEach(fitVideoToFrame), 200);
  });

  // Theme editor: sections that are re-rendered need a fresh pass
  doc.addEventListener('shopify:section:load', (event) => {
    mf.reveal(event.target);
    mf.inviewVideos(event.target);
  });
})();
